// Congés (M11) : demandes à valider (niveau chef pour les services dirigés, sans motif ; niveau RH après le
// chef, par une autre personne), historique et annulation motivée (RH habilités), nouvelle demande et
// règles de décompte versionnées (RH). Un refus est toujours motivé.
import { ETATS_CONGE, LIBELLES_ETAT_CONGE, LIBELLES_TYPE_CONGE, type CongeVueValidation } from '@cc/contrats';
import { Bandeau, Bouton, Champ, DialogueConfirmation, EtatVide, Filtres, LienBouton, Onglets, Panneau, PuceEtat, Tableau, useFiltresPersistes, type ColonneTableau, type Onglet } from '@cc/ui';
import { ArrowLeft, CalendarCheck } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { annulerConge, deciderConge, INVALIDER_PERSONNEL, useCongesAValider, useHistoriqueConges } from './api';
import { NouvelleDemande, ReglesConges } from './FormulairesConges';
import { jours, periode, TON_CONGE } from './logique';

type Decision = { conge: CongeVueValidation; niveau: 'chef' | 'rh'; decision: 'valider' | 'refuser' };

const colonnesBase = (action?: (c: CongeVueValidation) => ReactNode): ColonneTableau<CongeVueValidation>[] => [
  { id: 'agent', entete: 'Agent', enTeteLigne: true, masquable: false, cellule: (c) => `${c.nom} ${c.prenoms} (${c.matricule})`, valeurTri: (c) => c.nom },
  { id: 'service', entete: 'Service', cellule: (c) => c.service_nom },
  { id: 'type', entete: 'Type', cellule: (c) => LIBELLES_TYPE_CONGE[c.type] },
  { id: 'periode', entete: 'Période', cellule: (c) => periode(c), valeurTri: (c) => c.debut },
  { id: 'jours', entete: 'Durée', numerique: true, cellule: (c) => jours(c.demi_journees) },
  { id: 'etat', entete: 'État', cellule: (c) => <PuceEtat ton={TON_CONGE[c.etat]} libelle={LIBELLES_ETAT_CONGE[c.etat]} /> },
  ...(action ? [{ id: 'action', entete: 'Action', cellule: action }] : []),
];

function DialogueDecision({ d, onFermer }: { d: Decision; onFermer: () => void }) {
  const [motif, setMotif] = useState('');
  const action = useAction({
    executer: () => deciderConge(d.conge.id, d.niveau, { decision: d.decision, motif: motif.trim() || undefined }),
    succes: d.decision === 'valider' ? 'Décision de validation enregistrée.' : 'Refus motivé enregistré.',
    invalider: INVALIDER_PERSONNEL,
    apres: onFermer,
  });
  const refus = d.decision === 'refuser';
  return (
    <DialogueConfirmation ouvert titre={`${refus ? 'Refuser' : 'Valider'} la demande (niveau ${d.niveau === 'chef' ? 'chef de service' : 'ressources humaines'})`}
      message={`${d.conge.nom} ${d.conge.prenoms}, ${periode(d.conge)}, ${jours(d.conge.demi_journees)}.${d.niveau === 'rh' && !refus ? ' La validation RH débite le solde si la règle l\'exige.' : ''}`}
      libelleConfirmer={refus ? 'Refuser' : 'Valider'} variante={refus ? 'danger' : 'principal'} enCours={action.enCours}
      onConfirmer={() => action.lancer(undefined)} onAnnuler={onFermer} confirmationDesactivee={refus && motif.trim().length < 3}>
      <Champ libelle={refus ? 'Motif du refus' : 'Observation'} value={motif} onChange={(e) => setMotif(e.target.value)} required={refus} facultatif={!refus} />
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </DialogueConfirmation>
  );
}

function AValider() {
  const liste = useCongesAValider();
  const [decision, setDecision] = useState<Decision | null>(null);
  const boutons = (niveau: 'chef' | 'rh') => (c: CongeVueValidation) => (
    <span>
      <Bouton variante="principal" onClick={() => setDecision({ conge: c, niveau, decision: 'valider' })}>Valider</Bouton>{' '}
      <Bouton variante="secondaire" onClick={() => setDecision({ conge: c, niveau, decision: 'refuser' })}>Refuser</Bouton>
    </span>
  );
  const d = liste.data;
  return (
    <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} lot="L5-A" fonction="Les demandes à valider">
      {d ? (
        <>
          <Panneau titre="Niveau chef de service" niveau={2}>
            {d.services_diriges === 0 ? <p>Vous n&apos;êtes responsable d&apos;aucun service actif : aucune demande ne vous revient à ce niveau.</p>
              : d.chef.length === 0 ? <EtatVide titre="Aucune demande à valider" icone={CalendarCheck}><p>Aucune demande de vos services n&apos;attend votre décision.</p></EtatVide>
                : <Tableau legende="Demandes à valider par le chef de service" colonnes={colonnesBase(boutons('chef'))} lignes={d.chef} cleLigne={(c) => c.id} cleColonnes="personnel-a-valider-chef" />}
          </Panneau>
          {d.rh.length > 0 ? (
            <Panneau titre="Niveau ressources humaines" niveau={2}>
              <Tableau legende="Demandes à valider par les ressources humaines" colonnes={colonnesBase(boutons('rh'))} lignes={d.rh} cleLigne={(c) => c.id} cleColonnes="personnel-a-valider-rh" />
            </Panneau>
          ) : null}
        </>
      ) : null}
      {decision ? <DialogueDecision d={decision} onFermer={() => setDecision(null)} /> : null}
    </EtatRequete>
  );
}

function Historique() {
  const { contexte } = useSession();
  const [filtres, setFiltres] = useFiltresPersistes('personnel-conges', { etat: '' });
  const liste = useHistoriqueConges(filtres);
  const [annulation, setAnnulation] = useState<CongeVueValidation | null>(null);
  const [motif, setMotif] = useState('');
  const annuler = useAction({ executer: (c: CongeVueValidation) => annulerConge(c.id, motif), succes: 'Congé annulé ; le solde consommé est restitué.', invalider: INVALIDER_PERSONNEL, apres: () => { setAnnulation(null); setMotif(''); } });
  const annulable = (c: CongeVueValidation) => ['demande', 'valide_chef', 'valide_rh'].includes(c.etat) && peut(contexte, 'personnel_sensible', 'modifier');
  return (
    <>
      <Filtres groupes={[{ id: 'etat', libelle: 'État', options: [{ valeur: '', libelle: 'Tous' }, ...ETATS_CONGE.map((e) => ({ valeur: e, libelle: LIBELLES_ETAT_CONGE[e] }))] }]} valeurs={filtres} onChanger={setFiltres} />
      <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} lot="L5-A" fonction="L'historique des congés">
        {(liste.data?.elements ?? []).length === 0 ? <EtatVide titre="Aucune demande" icone={CalendarCheck}><p>Aucune demande ne correspond à ce filtre.</p></EtatVide>
          : <Tableau legende="Historique des congés" colonnes={colonnesBase((c) => (annulable(c) ? <Bouton variante="secondaire" onClick={() => setAnnulation(c)}>Annuler</Bouton> : null))} lignes={liste.data?.elements ?? []} cleLigne={(c) => c.id} cleColonnes="personnel-conges" />}
      </EtatRequete>
      <DialogueConfirmation ouvert={annulation !== null} titre="Annuler le congé" message="L'annulation est tracée ; un solde déjà consommé est restitué." libelleConfirmer="Annuler le congé" variante="danger"
        enCours={annuler.enCours} onConfirmer={() => annulation && annuler.lancer(annulation)} onAnnuler={() => setAnnulation(null)} confirmationDesactivee={motif.trim().length < 3}>
        <Champ libelle="Motif de l'annulation" value={motif} onChange={(e) => setMotif(e.target.value)} required />
        {annuler.messageErreur ? <Bandeau type="erreur">{annuler.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>
    </>
  );
}

export function PageConges() {
  const { contexte } = useSession();
  const rh = peut(contexte, 'personnel_sensible', 'voir');
  const onglets: Onglet[] = [];
  if (peut(contexte, 'personnel', 'valider')) onglets.push({ id: 'a-valider', libelle: 'À valider', contenu: <AValider /> });
  if (rh) {
    onglets.push({ id: 'historique', libelle: 'Historique', contenu: <Historique /> });
    if (peut(contexte, 'personnel_sensible', 'creer')) onglets.push({ id: 'demande', libelle: 'Nouvelle demande', contenu: <NouvelleDemande /> });
    onglets.push({ id: 'regles', libelle: 'Règles de décompte', contenu: <ReglesConges /> });
  }
  return (
    <>
      <EnTetePage etiquette="Personnel" titre="Congés" description="Circuit à deux niveaux : le chef de service, puis les ressources humaines par une autre personne. Le solde n'est débité qu'à la validation RH."
        actions={<LienBouton href="/personnel" icone={ArrowLeft} variante="secondaire">Personnel</LienBouton>} />
      {onglets.length ? <Onglets libelle="Congés" onglets={onglets} /> : (
        <EtatVide titre="Accès non autorisé" icone={CalendarCheck}><p>La validation des congés exige la permission de valider sur le personnel ; l&apos;historique et la saisie exigent en plus l&apos;habilitation nominative « personnel sensible ».</p></EtatVide>
      )}
    </>
  );
}
